"use client"

import { useState } from "react"
import { CheckCircle2, Circle, ShieldCheck, FileText, Building2, Monitor } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"

interface ChecklistItem {
  id: string
  text: string
  category: "Administrative" | "Physical" | "Technical"
  subcategory: string
  explanation: string
  required: boolean
}

const securityRuleItems: ChecklistItem[] = [
  // Administrative Safeguards
  {
    id: "admin-1",
    text: "Risk Analysis (164.308(a)(1)(ii)(A))",
    category: "Administrative",
    subcategory: "Security Management Process",
    explanation: "Conduct an accurate and thorough assessment of potential risks and vulnerabilities to the confidentiality, integrity, and availability of ePHI.",
    required: true,
  },
  {
    id: "admin-2",
    text: "Risk Management (164.308(a)(1)(ii)(B))",
    category: "Administrative",
    subcategory: "Security Management Process",
    explanation: "Implement security measures sufficient to reduce risks and vulnerabilities to a reasonable and appropriate level.",
    required: true,
  },
  {
    id: "admin-3",
    text: "Sanction Policy (164.308(a)(1)(ii)(C))",
    category: "Administrative",
    subcategory: "Security Management Process",
    explanation: "Apply appropriate sanctions against workforce members who fail to comply with security policies and procedures.",
    required: true,
  },
  {
    id: "admin-4",
    text: "Information System Activity Review (164.308(a)(1)(ii)(D))",
    category: "Administrative",
    subcategory: "Security Management Process",
    explanation: "Implement procedures to regularly review records of information system activity (logs, audit reports, incident tracking reports).",
    required: true,
  },
  {
    id: "admin-5",
    text: "Assigned Security Responsibility (164.308(a)(2))",
    category: "Administrative",
    subcategory: "Security Officer",
    explanation: "Identify the security official who is responsible for the development and implementation of security policies and procedures.",
    required: true,
  },
  {
    id: "admin-6",
    text: "Authorization and Supervision (164.308(a)(3)(ii)(A))",
    category: "Administrative",
    subcategory: "Workforce Security",
    explanation: "Implement procedures for authorization and/or supervision of workforce members who work with ePHI or in locations where it might be accessed.",
    required: false,
  },
  {
    id: "admin-7",
    text: "Workforce Clearance Procedure (164.308(a)(3)(ii)(B))",
    category: "Administrative",
    subcategory: "Workforce Security",
    explanation: "Implement procedures to determine that the access of a workforce member to ePHI is appropriate.",
    required: false,
  },
  {
    id: "admin-8",
    text: "Termination Procedures (164.308(a)(3)(ii)(C))",
    category: "Administrative",
    subcategory: "Workforce Security",
    explanation: "Implement procedures for terminating access to ePHI when employment or other arrangement ends or when access is no longer required.",
    required: false,
  },
  {
    id: "admin-9",
    text: "Access Authorization (164.308(a)(4)(ii)(B))",
    category: "Administrative",
    subcategory: "Information Access Management",
    explanation: "Implement policies and procedures for granting access to ePHI through workstations, transactions, programs, or processes.",
    required: false,
  },
  {
    id: "admin-10",
    text: "Access Establishment and Modification (164.308(a)(4)(ii)(C))",
    category: "Administrative",
    subcategory: "Information Access Management",
    explanation: "Implement policies and procedures that establish, document, review, and modify a user's right of access to workstation, transaction, program, or process.",
    required: false,
  },
  {
    id: "admin-11",
    text: "Security Awareness and Training (164.308(a)(5)(i))",
    category: "Administrative",
    subcategory: "Security Training",
    explanation: "Implement a security awareness and training program for all members of the workforce (including management).",
    required: true,
  },
  {
    id: "admin-12",
    text: "Security Reminders (164.308(a)(5)(ii)(A))",
    category: "Administrative",
    subcategory: "Security Training",
    explanation: "Periodic security updates and reminders to workforce members.",
    required: false,
  },
  {
    id: "admin-13",
    text: "Protection from Malicious Software (164.308(a)(5)(ii)(B))",
    category: "Administrative",
    subcategory: "Security Training",
    explanation: "Procedures for guarding against, detecting, and reporting malicious software.",
    required: false,
  },
  {
    id: "admin-14",
    text: "Log-in Monitoring (164.308(a)(5)(ii)(C))",
    category: "Administrative",
    subcategory: "Security Training",
    explanation: "Procedures for monitoring log-in attempts and reporting discrepancies.",
    required: false,
  },
  {
    id: "admin-15",
    text: "Password Management (164.308(a)(5)(ii)(D))",
    category: "Administrative",
    subcategory: "Security Training",
    explanation: "Procedures for creating, changing, and safeguarding passwords.",
    required: false,
  },
  {
    id: "admin-16",
    text: "Security Incident Response (164.308(a)(6)(i))",
    category: "Administrative",
    subcategory: "Security Incidents",
    explanation: "Identify and respond to suspected or known security incidents; mitigate harmful effects; document incidents and outcomes.",
    required: true,
  },
  {
    id: "admin-17",
    text: "Data Backup Plan (164.308(a)(7)(ii)(A))",
    category: "Administrative",
    subcategory: "Contingency Plan",
    explanation: "Establish procedures to create and maintain retrievable exact copies of ePHI.",
    required: true,
  },
  {
    id: "admin-18",
    text: "Disaster Recovery Plan (164.308(a)(7)(ii)(B))",
    category: "Administrative",
    subcategory: "Contingency Plan",
    explanation: "Establish procedures to restore any loss of data.",
    required: true,
  },
  {
    id: "admin-19",
    text: "Emergency Mode Operation Plan (164.308(a)(7)(ii)(C))",
    category: "Administrative",
    subcategory: "Contingency Plan",
    explanation: "Establish procedures to enable continuation of critical business processes for protection of ePHI while operating in emergency mode.",
    required: true,
  },
  {
    id: "admin-20",
    text: "Testing and Revision Procedures (164.308(a)(7)(ii)(D))",
    category: "Administrative",
    subcategory: "Contingency Plan",
    explanation: "Implement procedures for periodic testing and revision of contingency plans.",
    required: false,
  },
  {
    id: "admin-21",
    text: "Applications and Data Criticality Analysis (164.308(a)(7)(ii)(E))",
    category: "Administrative",
    subcategory: "Contingency Plan",
    explanation: "Assess the relative criticality of specific applications and data in support of other contingency plan components.",
    required: false,
  },
  {
    id: "admin-22",
    text: "Evaluation (164.308(a)(8))",
    category: "Administrative",
    subcategory: "Evaluation",
    explanation: "Perform periodic technical and nontechnical evaluation of security measures in response to environmental or operational changes.",
    required: true,
  },
  {
    id: "admin-23",
    text: "Business Associate Contracts (164.308(b)(1))",
    category: "Administrative",
    subcategory: "Business Associates",
    explanation: "Obtain satisfactory assurances in writing that business associates will appropriately safeguard ePHI through Business Associate Agreements.",
    required: true,
  },

  // Physical Safeguards
  {
    id: "physical-1",
    text: "Contingency Operations (164.310(a)(2)(i))",
    category: "Physical",
    subcategory: "Facility Access Controls",
    explanation: "Establish procedures to allow facility access in support of restoration of lost data under disaster recovery and emergency mode operations.",
    required: false,
  },
  {
    id: "physical-2",
    text: "Facility Security Plan (164.310(a)(2)(ii))",
    category: "Physical",
    subcategory: "Facility Access Controls",
    explanation: "Implement policies and procedures to safeguard the facility and equipment from unauthorized physical access, tampering, and theft.",
    required: false,
  },
  {
    id: "physical-3",
    text: "Access Control and Validation Procedures (164.310(a)(2)(iii))",
    category: "Physical",
    subcategory: "Facility Access Controls",
    explanation: "Implement procedures to control and validate a person's access to facilities based on their role or function.",
    required: false,
  },
  {
    id: "physical-4",
    text: "Maintenance Records (164.310(a)(2)(iv))",
    category: "Physical",
    subcategory: "Facility Access Controls",
    explanation: "Implement policies and procedures to document repairs and modifications to physical components of facility related to security.",
    required: false,
  },
  {
    id: "physical-5",
    text: "Workstation Use (164.310(b))",
    category: "Physical",
    subcategory: "Workstation Controls",
    explanation: "Implement policies specifying proper functions to be performed, manner of performance, and physical attributes of surroundings for workstations accessing ePHI.",
    required: true,
  },
  {
    id: "physical-6",
    text: "Workstation Security (164.310(c))",
    category: "Physical",
    subcategory: "Workstation Controls",
    explanation: "Implement physical safeguards for all workstations that access ePHI to restrict access to authorized users.",
    required: true,
  },
  {
    id: "physical-7",
    text: "Disposal (164.310(d)(2)(i))",
    category: "Physical",
    subcategory: "Device and Media Controls",
    explanation: "Implement policies and procedures to address final disposition of ePHI and/or hardware or electronic media containing ePHI.",
    required: true,
  },
  {
    id: "physical-8",
    text: "Media Re-use (164.310(d)(2)(ii))",
    category: "Physical",
    subcategory: "Device and Media Controls",
    explanation: "Implement procedures for removal of ePHI from electronic media before media is made available for re-use.",
    required: true,
  },
  {
    id: "physical-9",
    text: "Accountability (164.310(d)(2)(iii))",
    category: "Physical",
    subcategory: "Device and Media Controls",
    explanation: "Maintain record of movements of hardware and electronic media and any person responsible for movements.",
    required: false,
  },
  {
    id: "physical-10",
    text: "Data Backup and Storage (164.310(d)(2)(iv))",
    category: "Physical",
    subcategory: "Device and Media Controls",
    explanation: "Create retrievable, exact copy of ePHI when needed before movement of equipment.",
    required: false,
  },

  // Technical Safeguards
  {
    id: "technical-1",
    text: "Unique User Identification (164.312(a)(2)(i))",
    category: "Technical",
    subcategory: "Access Control",
    explanation: "Assign unique name and/or number for identifying and tracking user identity.",
    required: true,
  },
  {
    id: "technical-2",
    text: "Emergency Access Procedure (164.312(a)(2)(ii))",
    category: "Technical",
    subcategory: "Access Control",
    explanation: "Establish procedures for obtaining necessary ePHI during an emergency.",
    required: true,
  },
  {
    id: "technical-3",
    text: "Automatic Logoff (164.312(a)(2)(iii))",
    category: "Technical",
    subcategory: "Access Control",
    explanation: "Implement electronic procedures to terminate an electronic session after a predetermined time of inactivity.",
    required: false,
  },
  {
    id: "technical-4",
    text: "Encryption and Decryption (164.312(a)(2)(iv))",
    category: "Technical",
    subcategory: "Access Control",
    explanation: "Implement mechanism to encrypt and decrypt ePHI. NOW MANDATORY as of January 2025 updates.",
    required: true,
  },
  {
    id: "technical-5",
    text: "Multi-Factor Authentication (MFA)",
    category: "Technical",
    subcategory: "Access Control",
    explanation: "Implement MFA for all systems accessing ePHI. MANDATORY as of January 2025 Security Rule updates.",
    required: true,
  },
  {
    id: "technical-6",
    text: "Audit Controls (164.312(b))",
    category: "Technical",
    subcategory: "Audit and Monitoring",
    explanation: "Implement hardware, software, and/or procedural mechanisms to record and examine activity in information systems containing ePHI.",
    required: true,
  },
  {
    id: "technical-7",
    text: "Integrity Controls (164.312(c)(1))",
    category: "Technical",
    subcategory: "Integrity",
    explanation: "Implement policies and procedures to protect ePHI from improper alteration or destruction.",
    required: true,
  },
  {
    id: "technical-8",
    text: "Mechanism to Authenticate ePHI (164.312(c)(2))",
    category: "Technical",
    subcategory: "Integrity",
    explanation: "Implement electronic mechanisms to corroborate that ePHI has not been altered or destroyed in an unauthorized manner.",
    required: false,
  },
  {
    id: "technical-9",
    text: "Person or Entity Authentication (164.312(d))",
    category: "Technical",
    subcategory: "Authentication",
    explanation: "Implement procedures to verify that a person or entity seeking access to ePHI is the one claimed.",
    required: true,
  },
  {
    id: "technical-10",
    text: "Transmission Security - Integrity Controls (164.312(e)(2)(i))",
    category: "Technical",
    subcategory: "Transmission Security",
    explanation: "Implement security measures to ensure electronically transmitted ePHI is not improperly modified without detection.",
    required: false,
  },
  {
    id: "technical-11",
    text: "Transmission Security - Encryption (164.312(e)(2)(ii))",
    category: "Technical",
    subcategory: "Transmission Security",
    explanation: "Implement mechanism to encrypt ePHI whenever deemed appropriate. NOW MANDATORY for network transmission.",
    required: true,
  },
  {
    id: "technical-12",
    text: "Network Segmentation",
    category: "Technical",
    subcategory: "Transmission Security",
    explanation: "Implement network segmentation to isolate systems containing ePHI. Emphasized in January 2025 updates.",
    required: false,
  },
]

export default function SecurityRuleChecklistContent() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())
  const [activeCategory, setActiveCategory] = useState<"Administrative" | "Physical" | "Technical">("Administrative")

  const toggleItem = (id: string) => {
    const newChecked = new Set(checkedItems)
    if (newChecked.has(id)) {
      newChecked.delete(id)
    } else {
      newChecked.add(id)
    }
    setCheckedItems(newChecked)
  }

  const categories: Array<"Administrative" | "Physical" | "Technical"> = ["Administrative", "Physical", "Technical"]

  const getCategoryIcon = (category: string) => {
    if (category === "Administrative") return FileText
    if (category === "Physical") return Building2
    return Monitor
  }

  const getCategoryProgress = (category: "Administrative" | "Physical" | "Technical") => {
    const items = securityRuleItems.filter(item => item.category === category)
    const checked = items.filter(item => checkedItems.has(item.id)).length
    return { checked, total: items.length, percentage: Math.round((checked / items.length) * 100) }
  }

  const overallProgress = Math.round((checkedItems.size / securityRuleItems.length) * 100)

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white hover:bg-white/30 mb-6">
              <ShieldCheck className="mr-2 h-4 w-4" />
              HIPAA Security Rule
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Security Rule Compliance Checklist</h1>
            <p className="text-xl text-white/90">
              Complete checklist of all Administrative, Physical, and Technical Safeguards required by the HIPAA Security Rule (45 CFR Part 164, Subpart C). Track your compliance status with official regulation references.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Overview */}
      <section className="py-8 bg-[#F0F9FF]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 border-[#0EA5E9] mb-8">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-[#0F172A]">Overall Progress</h2>
                  <span className="text-3xl font-bold text-[#0EA5E9]">{overallProgress}%</span>
                </div>
                <div className="w-full bg-[#E2E8F0] rounded-full h-4 mb-4">
                  <div
                    className="bg-[#0EA5E9] h-4 rounded-full transition-all duration-300"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <p className="text-sm text-[#64748B] mb-6">
                  {checkedItems.size} of {securityRuleItems.length} requirements completed
                </p>

                {/* Category Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {categories.map((category) => {
                    const { checked, total, percentage } = getCategoryProgress(category)
                    const Icon = getCategoryIcon(category)
                    return (
                      <div key={category} className="bg-white rounded-lg p-4 border border-[#E2E8F0]">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0F172A]">{category}</h3>
                            <p className="text-xs text-[#64748B]">{checked}/{total}</p>
                          </div>
                        </div>
                        <div className="w-full bg-[#E2E8F0] rounded-full h-2">
                          <div
                            className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="border border-[#E2E8F0] mb-8">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#DC2626] text-white">Required</Badge>
                    <span className="text-sm text-[#64748B]">Mandatory implementation specification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#F59E0B] text-white">Addressable</Badge>
                    <span className="text-sm text-[#64748B]">Must implement or document alternative</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Tab Buttons */}
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((category) => {
                const Icon = getCategoryIcon(category)
                const { checked, total } = getCategoryProgress(category)
                return (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      activeCategory === category
                        ? "bg-[#0EA5E9] text-white"
                        : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {category} Safeguards ({checked}/{total})
                  </button>
                )
              })}
            </div>

            {/* Checklist Items */}
            <Card className="border-2 border-[#E2E8F0]">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {Array.from(new Set(securityRuleItems
                    .filter(item => item.category === activeCategory)
                    .map(item => item.subcategory)))
                    .map((subcategory) => {
                      const subcategoryItems = securityRuleItems.filter(
                        item => item.category === activeCategory && item.subcategory === subcategory
                      )
                      return (
                        <div key={subcategory} className="border-l-4 border-[#0EA5E9] pl-4">
                          <h3 className="font-bold text-[#0F172A] mb-3 text-lg">{subcategory}</h3>
                          <div className="space-y-3">
                            {subcategoryItems.map((item) => (
                              <div
                                key={item.id}
                                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  checkedItems.has(item.id)
                                    ? "bg-[#F0FDF4] border-[#86EFAC]"
                                    : "bg-white border-[#E2E8F0] hover:border-[#0EA5E9]"
                                }`}
                                onClick={() => toggleItem(item.id)}
                              >
                                <div className="flex items-start gap-3">
                                  {checkedItems.has(item.id) ? (
                                    <CheckCircle2 className="h-6 w-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Circle className="h-6 w-6 text-[#94A3B8] flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <h4 className={`font-bold ${
                                        checkedItems.has(item.id) ? "text-[#15803D]" : "text-[#0F172A]"
                                      }`}>
                                        {item.text}
                                      </h4>
                                      <Badge className={item.required ? "bg-[#DC2626] text-white" : "bg-[#F59E0B] text-white"}>
                                        {item.required ? "Required" : "Addressable"}
                                      </Badge>
                                    </div>
                                    <p className={`text-sm ${
                                      checkedItems.has(item.id) ? "text-[#15803D]" : "text-[#64748B]"
                                    }`}>
                                      {item.explanation}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Get Expert Help with Security Rule Compliance</h2>
            <p className="text-xl text-white/90 mb-8">
              Our HIPAA compliance specialists can help you implement every safeguard and ensure your organization meets all Security Rule requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/self-assessment">
                <Button size="lg" className="bg-white text-[#0EA5E9] hover:bg-white/90">
                  Take Security Assessment
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" className="bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#0EA5E9]">
                  Schedule Consultation
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
